import React from "react";
import { fmtDatetime, toZonedDateTime } from "utils/time-utils";
import { Cell, Month, WeekDay } from "./TimelineComponents";
import { useTimezone } from "hooks/useTimezone";
import { DATE_FORMATTER_DAYS, DATE_FORMATTER_NO_TIME } from "utils/constants";
import { Duration, Instant, YearMonth } from "@js-joda/core";

interface TimelineProps {
  range: [Date, Date];
  data: { date: Date; value: number }[];
  ignoreCurrentDay: boolean;
  colorFunc: (value: number) => string;
  decimalPlaces?: number;
}

const Timeline = ({ range, data, ignoreCurrentDay, colorFunc, decimalPlaces }: TimelineProps) => {
  const tz = useTimezone();

  const start = toZonedDateTime(range[0], tz.company);
  const end = toZonedDateTime(range[1], tz.company);

  const days = Duration.between(start, end).toDays() + 1;

  const startDayOfWeek = start.dayOfWeek().value() - 1
  const endDayOfWeek = end.dayOfWeek().value() - 1

  const totalCells = days + startDayOfWeek + (6 - endDayOfWeek);
  const weeks = Math.ceil(totalCells / 7);
  const cells = Array.from(new Array(totalCells));
  const weekDays = Array.from(new Array(7).keys());
  const months = Array.from(new Array(weeks));

  const startDate = toZonedDateTime(range[0], tz.company);

  return (
    <div className="timeline">
      <div className="timeline-months">
        {months.map((_, index) => (
          <Month key={index} index={index} startDate={startDate} />
        ))}
      </div>

      <div className="timeline-body">
        <div className="timeline-weekdays">
          {weekDays.map(index => (
            <WeekDay key={index} index={index} />
          ))}
        </div>

        <div className="timeline-cells">
          {cells.map((_, index) => {
            const adjustedIndex = index - startDayOfWeek;
            if (adjustedIndex < 0 || adjustedIndex >= days) {
              return <Cell key={adjustedIndex} color="transparent" />;
            }

            const date = start.plusDays(adjustedIndex);

            const normalizedDate = date
              .withHour(0)
              .withMinute(0)
              .withSecond(0)
              .withNano(0);

            const dataPoint = data.find(d => {
              const dZoned = toZonedDateTime(d.date, tz.company).withHour(0).withMinute(0).withSecond(0).withNano(0);
              return dZoned.toInstant().equals(normalizedDate.toInstant());
            });

            const dateString = fmtDatetime(date, tz.display, DATE_FORMATTER_DAYS);

            let color = "#ffffff";
            const today = toZonedDateTime(Instant.now(), tz.company).withHour(0).withMinute(0).withSecond(0).withNano(0);

            if (ignoreCurrentDay && date.equals(today)) {
              color = "";
            } else if (dataPoint) {
              color = colorFunc(dataPoint.value);
            }

            const value = dataPoint ? dataPoint.value : null;

            const isFirstMonthDay = adjustedIndex === 0;

            const yearMonth = YearMonth.of(date.year(), date.month());
            const lastDayInMonth = date.withDayOfMonth(yearMonth.lengthOfMonth());

            const isMonday = date.dayOfWeek().value() === 1;
            const isSunday = date.dayOfWeek().value() === 7;

            let className = "";
            if (date.dayOfMonth() > lastDayInMonth.dayOfMonth() - 7) className += " month-end";
            if (isMonday) className += " monday";
            if (isSunday) className += " sunday";
            if (isFirstMonthDay) className += " first-month-day";
            if (date.equals(lastDayInMonth)) className += " last-month-day";
            if (adjustedIndex < 7) className += " first-week";

            return (
              <Cell key={adjustedIndex} color={color} className={className} value={value ?? undefined} date={dateString} decimalPlaces={decimalPlaces} />);
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
